import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import './ICPHub.css'

const PRODUCTS = [
  { id: 'aisec', name: 'AISec Training', icon: '🎓' },
  { id: 'saas', name: 'SaaS Security Score', icon: '🔒' }
]

const sections = [
  { id: 'summary', label: 'ICP Summary', icon: '🎯' },
  { id: 'messaging', label: 'Messaging Map', icon: '💬' },
  { id: 'qualification', label: 'Qualification Checklist', icon: '✅' },
  { id: 'pricing', label: 'Pricing & Tiers', icon: '💰' },
  { id: 'channels', label: 'Where to Find ICP', icon: '📍' },
  { id: 'website', label: 'Website Structure', icon: '🌐' },
  { id: 'triggers', label: 'Buying Triggers', icon: '⚡' },
  { id: 'templates', label: 'SDR Templates', icon: '📋' }
]

const productData = {
  saas: {
    title: 'SaaS Security Score',
    subtitle: 'Everything sales and business development reps need to qualify, message, and close deals',
    icpSummary: `SaaS Security Score is ideal for mid-market, SaaS-heavy organizations (100–500 employees) operating in regulated or data-sensitive industries that must manage security posture, compliance, and vendor risk across dozens or hundreds of SaaS applications. These organizations have growing audit pressure, fragmented security documentation, limited visibility into SaaS risk and data residency, and leadership demanding measurable security outcomes. The platform is bought by security and GRC leaders who need a single source of truth for SaaS governance, compliance tracking, incident management, and security ROI.`,
    profile: {
      company: [
        { strong: 'Size:', text: '50–1,000 employees (sweet spot: 100–500)' },
        { strong: 'Revenue:', text: '$10M–$250M' },
        { strong: 'SaaS footprint:', text: '30–300 applications' },
        { strong: 'Industries:', text: 'Tech/SaaS, Fintech, Healthtech, E-commerce, Professional services' },
        { strong: 'Geography:', text: 'EU, UK, North America, APAC (multi-region ideal)' }
      ],
      personas: [
        { strong: 'Primary:', text: 'Head of Security, GRC Manager, Security Architect' },
        { strong: 'Secondary:', text: 'CTO, VP Engineering, CIO' },
        { strong: 'Executive:', text: 'CISO, CFO' }
      ],
      nonIcp: ['<20 employees, minimal SaaS', 'Heavily on-prem, low SaaS adoption', 'Wants vulnerability scanning, EDR replacement', 'Checkbox-only compliance, no governance']
    },
    messaging: [
      { persona: 'Primary: Head of Security / GRC Manager', pains: ['No single view of SaaS risk', "Spreadsheets don't scale", 'Audits are painful and time-consuming', 'Vendor security questionnaires are repetitive', 'Hard to show progress quarter over quarter'], messages: ['"Replace spreadsheets with a real SaaS security control plane"', '"See posture, compliance, incidents, and vendors in one dashboard"', '"Turn security processes into measurable scores"'] },
      { persona: 'Secondary: CTO / VP Engineering', pains: ['Security slowing teams down', 'Lack of clarity on SaaS risk exposure', 'Budget scrutiny'], messages: ['"Know your SaaS risk without blocking teams"', '"Understand where your data lives globally"', '"Align security investment with business impact"'] },
      { persona: 'Executive: CISO / CFO', pains: ['Inability to answer "Are we safer than last quarter?"', 'Justifying security spend', 'Exposure to regulatory and data sovereignty risk'], messages: ['"Quantify SaaS security risk in business terms"', '"Track compliance and risk trends over time"', '"Prove ROI on security investment"'] }
    ],
    qualification: { firmographic: ['50–1,000 employees', '30+ SaaS applications', 'Handles customer, payment, or regulated data'], compliance: ['SOC 2 / ISO 27001 required or planned', 'Audit within next 6 months', 'Customer security questionnaires increasing'], pain: ['Uses spreadsheets for vendor or SaaS risk', 'No clear SaaS data residency view', 'No unified incident tracking for SaaS', 'No way to quantify security progress'], authority: ['Security / GRC owns decision', 'CTO / CISO involved', 'Budget exists for security tooling'], disqualify: ['<20 employees', 'Wants vulnerability scanning or EDR replacement', 'No compliance or governance requirements'] },
    bantLink: 'SaaS Security Score',
    tiers: [
      { name: 'Essential — "Get Control"', target: '50–150 employees, first SOC 2 / ISO effort', value: 'Replace spreadsheets, establish baseline governance', features: ['SaaS inventory', 'Basic posture scoring', 'Compliance tracking', 'Assessments', 'Document templates', 'MFA & audit logs'], positioning: '"Know your SaaS security baseline."' },
      { name: 'Business — "Operationalize Security"', target: '150–500 employees, ongoing compliance + audits', value: 'Continuous governance, risk-driven prioritization', features: ['Incident management', 'Threat intelligence (CVE / KEV)', 'Data sovereignty mapping', 'Spend & renewal tracking', 'Advanced analytics'], positioning: '"Continuously govern SaaS risk."' },
      { name: 'Enterprise — "Prove Risk Reduction"', target: '500–1,000 employees, multi-region, multi-framework', value: 'Executive reporting, ROI and board visibility', features: ['Security budget & ROI', 'API integrations', 'Advanced alerts', 'Custom reporting', 'Priority support'], positioning: '"Quantify and prove SaaS security value."' }
    ],
    channels: [
      { title: 'LinkedIn (Highest ROI)', content: [{ strong: 'Target:', text: 'Head of Security, GRC Manager, Security Architect, CISO (mid-market) — 50–1,000 employees. Industries: SaaS, Fintech, Healthtech, E-commerce, Professional Services.' }, { strong: 'Engage:', text: 'Founder-led content on SOC 2 / ISO audits, vendor risk, compliance fatigue. Soft CTA: "See how we score SaaS security posture."' }] },
      { title: 'GRC & Security Communities (High-Intent)', content: [{ text: 'r/GRC, r/netsec, GRC Slack/Discord, CISO chapters, vendor risk forums. Educate first — share templates, frameworks. Introduce product after value.' }] },
      { title: 'Search & Lead Magnets', content: [{ strong: 'Keywords:', text: '"SOC 2 SaaS vendor risk", "ISO 27001 SaaS assessment", "SSPM vs GRC"' }, { strong: 'Lead magnets:', text: 'SaaS Security Scorecard (PDF or interactive)' }] },
      { title: 'Auditor & Consultant Ecosystem', content: [{ text: 'Partner with SOC 2 auditors, ISO consultants, vCISOs. Offer free internal licenses, co-branded templates. Position as "client governance layer".' }] },
      { title: 'Where NOT to Spend Time (Early On)', list: ['Broad IT forums', 'Generic startup communities', 'Feature-comparison sites without context', 'Cold outreach without compliance relevance'] }
    ],
    website: [
      { title: 'Hero', detail: '"Continuous security governance for your entire SaaS estate"', cta: 'CTA: "Get your SaaS Security Score"' },
      { title: 'The Problem', detail: 'SaaS sprawl broke your security visibility' },
      { title: 'The Solution', detail: 'One platform: posture scoring, compliance, assessments, data sovereignty, incidents, ROI' },
      { title: 'How It Works', detail: 'Inventory → Measure → Act' },
      { title: 'Compliance Coverage', detail: 'Track 8+ standards (SOC 2, ISO, PCI, HIPAA, NIST, CIS, OWASP)' },
      { title: 'Proof & Trust', detail: 'MFA, audit logs, tenant isolation' },
      { title: 'Final CTA', detail: '"Replace spreadsheets. See your SaaS risk clearly."' }
    ],
    triggers: [
      { title: 'Compliance & Audit', items: ['SOC 2 / ISO audit scheduled within 3–6 months', 'New security or GRC hire needing structure fast'] },
      { title: 'SaaS Sprawl', items: ['Rapid growth, M&A, or remote work driving SaaS adoption', 'No unified view of SaaS risk or spend'] },
      { title: 'Incident & Pressure', items: ['Recent vendor incident, data breach, or compliance finding', 'CFO pressure to control SaaS spend or justify security budget'] }
    ]
  },
  aisec: {
    title: 'AISec Training',
    subtitle: 'Ideal customer profile for AI security training & certifications — individual and enterprise',
    differentiator: 'Largest training catalog and security certifications geared for AI security.',
    icpSummary: `AISec Training is ideal for two core segments: (1) Individual learners — developers, security engineers, and professionals seeking AI security certification, career advancement, or upskilling for regulatory and employer demand; (2) Enterprise buyers — mid-market to large organizations (10+ users) that need AI governance, regulatory readiness (EU AI Act, NIS2), secure AI development capability, and team-wide standardisation. Geography spans high-spend markets (US, UK, Germany, France, Netherlands, Sweden, Switzerland, Singapore, UAE) and individual-led markets (India, Poland, Spain, Brazil). The product is bought by CISOs, Heads of Security, Compliance, and Learning & Development for teams; and by individuals for certification and career differentiation.`,
    profile: {
      company: [
        { strong: 'Individual:', text: 'Developers, security engineers, consultants; certification or career focus' },
        { strong: 'Enterprise size:', text: '10+ users (Team/Enterprise); sweet spot 20–50 for starting out' },
        { strong: 'Industries:', text: 'Tech/SaaS, Fintech, Finance, Government, Manufacturing, Consulting' },
        { strong: 'Geography:', text: 'US, UK, EU (DE, FR, NL, SE, CH), Singapore, UAE, India, APAC' },
        { strong: 'Buyer type:', text: 'Enterprise-first in US/UK/EU; Individual-first in India, Brazil, Romania' }
      ],
      personas: [
        { strong: 'Individual:', text: 'Self-decision maker; certification prep, AI security upskilling, career change' },
        { strong: 'Enterprise primary:', text: 'CISO, Head of Security, AI Risk Lead, Compliance, L&D' },
        { strong: 'Enterprise influencer:', text: 'CTO, VP Engineering, Risk, Security Architecture' }
      ],
      nonIcp: ['No interest in AI or security training', 'Wants generic cyber awareness only (no AI)', 'No budget or approval path for training', 'One-off course buyer with no certification or team need']
    },
    messaging: [
      { persona: 'Individual: Developer / Security Engineer', pains: ['AI security skills gap', 'Certification for career advancement', 'Employability as AI becomes mandatory'], messages: ['"Largest training catalog and security certifications geared for AI security"', '"AI security is becoming a differentiator for senior roles"', '"Structured certification, not just awareness"' ] },
      { persona: 'Enterprise: CISO / Head of Security', pains: ['AI governance and regulatory readiness (EU AI Act, NIS2)', 'Auditors will ask; need defensible training', 'Team upskilling at scale'], messages: ['"Largest AI security training catalog and certifications — built for audit and governance"', '"Defensible AI security capability with certifications and audit-ready reporting"', '"Replace ad-hoc training with a verifiable program"' ] },
      { persona: 'Enterprise: CTO / VP Engineering', pains: ['Security slowing teams; need practical, not checkbox', 'Understanding AI-specific risks', 'Budget justification for training'], messages: ['"Largest AI security training catalog — practical, not checkbox"', '"Practical AI security framework mapped to regulations"', '"Certifications pooled, role-agnostic; use when needed"' ] }
    ],
    qualification: { firmographic: ['Individual: pays personally or employer reimbursement', 'Enterprise: 10+ users or training budget allocated', 'Industry uses or handles AI / customer data'], compliance: ['EU AI Act, NIS2, or regulatory prep', 'Audit or compliance deadline', 'Customer/partner security questionnaires'], pain: ['No structured AI security training today', 'Existing training doesn\'t cover AI-specific risks', 'Need certification or evidence for audits'], authority: ['Individual: self-decision maker', 'Enterprise: Security/L&D/Compliance owns decision or strong influencer'], disqualify: ['Wants only generic awareness with no AI focus', 'No budget or approval path', 'One-off course, no certification or team rollout'] },
    bantLink: 'AISec Training',
    tiers: [
      { name: 'Individual Subscriber', target: 'Single learner; certification or upskilling', value: 'Certification prep, AI security skills, career advancement', features: ['Courses + labs', 'Certification exam access', 'Self-paced', 'Verifiable credential'], positioning: '"Get certified. Stand out."' },
      { name: 'Enterprise Core', target: '10–20 users; courses + labs', value: 'Baseline AI security capability without certification pressure', valueNote: '$1,999 / year', features: ['10–20 users', 'Courses + labs', 'No certification in Core (upgrade for certs)'], positioning: '"Know your baseline."' },
      { name: 'Enterprise Professional', target: '21–50 users; certifications included', value: 'Formal assurance and internal validation', valueNote: '$4,999 / year', features: ['21–50 users', 'Courses + labs + certifications', 'Audit alignment', 'Seat buffers negotiable'], positioning: '"Team capability with credentials."' },
      { name: 'Enterprise Advanced', target: '50+ users; custom', value: 'Scale, price lock, enhanced certification access', valueNote: 'From $9,999 / year', features: ['50+ users', 'Custom terms', 'Certification rollover', 'Priority support'], positioning: '"Enterprise-wide AI security readiness."' }
    ],
    channels: [
      { title: 'LinkedIn (Highest ROI)', content: [{ strong: 'Target:', text: 'CISO, Head of Security, GRC, AI Risk Lead, Developers (by region). Use regional DM scripts — EU (DE, FR, NL, SE, UK), US, India, Singapore, UAE.' }, { strong: 'Engage:', text: 'Educational content on EU AI Act, NIS2, AI security readiness. Soft CTA: "See how we structure AI security training."' }] },
      { title: 'Security & GRC Communities', content: [{ text: 'r/GRC, r/netsec, CISO chapters, security leadership roundtables. Share frameworks and certification value before product.' }] },
      { title: 'Search & Lead Magnets', content: [{ strong: 'Keywords:', text: '"AI security certification", "EU AI Act training", "AI governance training", "secure AI development"' }, { strong: 'Lead magnets:', text: 'Certification overview, AI security readiness checklist' }] },
      { title: 'Conferences & Events', content: [{ text: 'Security and compliance conferences, SaaS governance webinars, virtual CISO summits. Educational talks: "Measuring AI security maturity", "From compliance to continuous governance".' }] },
      { title: 'Where NOT to Focus (Early On)', list: ['Generic IT training forums', 'Pure DevOps without security/compliance angle', 'Cold outreach without regulatory or certification relevance'] }
    ],
    website: [
      { title: 'Hero', detail: 'Artificial Intelligence (AI) Security Training & Services', cta: 'CTA: Explore training / Get certified' },
      { title: 'Value proposition', detail: 'Largest training catalog and security certifications geared for AI security. Structured training and audit-ready capability — not generic awareness.' },
      { title: 'Training / Courses', detail: 'Course catalog (aisectraining.com/training); compliance awareness, secure coding, incident response' },
      { title: 'Certifications', detail: 'AISec AI Security Certifications — verifiable credentials; link to certification page' },
      { title: 'Enterprise', detail: 'Team and enterprise tiers; Core, Professional, Advanced' },
      { title: 'Proof & Trust', detail: 'Global presence (US, Dubai, Malaysia, Singapore); "our team"' },
      { title: 'Final CTA', detail: 'Start training / Request demo / View certifications' }
    ],
    triggers: [
      { title: 'Regulatory & Audit', items: ['EU AI Act, NIS2, or similar regulation coming into force', 'Audit or compliance deadline', 'Auditors asking for AI governance evidence'] },
      { title: 'Career & Team', items: ['Individual: exam or career deadline', 'Enterprise: new GRC/security hire needing structure', 'Team rollout in < 3–6 months'] },
      { title: 'Risk & Demand', items: ['Recent AI incident or board pressure', 'Customer/partner security questionnaires increasing', 'Desire for certification over ad-hoc training'] }
    ],
    sdrTemplates: [
      {
        persona: 'Individual — Developer / Security Engineer',
        templates: [
          { type: 'LinkedIn DM', label: 'Opener', text: 'Hi {{Name}}, quick question, are you already working with AI systems or planning to move into AI-related security work? We run the largest AI security training catalog and certifications; happy to share what skills are becoming most requested if useful.' },
          { type: 'LinkedIn DM', label: 'Follow-up', text: 'Hi {{Name}}, following up, AI security is becoming a real differentiator for senior engineering and security roles. If you\'d like a short roadmap of what companies are starting to expect, I can send it over.' },
          { type: 'Email', label: 'Intro', subject: 'AI security certification, skills companies are asking for', text: 'Hi {{Name}},\n\nI noticed your background in security/engineering and wanted to reach out. We offer the largest training catalog and security certifications geared for AI security, something more teams are asking for as the EU AI Act and similar regulations land.\n\nIf you\'re exploring certification or upskilling in AI security, I\'d be glad to send a short overview of our programs. No pitch, just the resource.\n\nBest,\n{{YourName}}' }
        ]
      },
      {
        persona: 'Enterprise — CISO / Head of Security',
        templates: [
          { type: 'LinkedIn DM', label: 'Opener', text: 'Hi {{Name}}, with the EU AI Act and NIS2 coming into force, are your teams already receiving structured AI security or governance training, or is that still being assessed internally? We help orgs with a practical framework and the largest AI security certification catalog, built for audit and governance.' },
          { type: 'LinkedIn DM', label: 'Value + CTA', text: 'Many teams we speak to find that existing security training doesn\'t fully cover AI-specific risks (model misuse, data leakage, governance). We help close that gap with verifiable certifications and audit-ready reporting. If useful, I can share a short overview of how others are structuring AI security readiness.' },
          { type: 'Email', label: 'Intro', subject: 'AI security readiness, defensible training for auditors', text: 'Hi {{Name}},\n\nAs auditors and regulators start asking explicitly about AI governance, many security and GRC leaders are looking for defensible training, not just awareness.\n\nWe offer the largest AI security training catalog and certifications geared for audit and governance. Teams use us to replace ad-hoc training with a verifiable program and evidence for audits.\n\nIf you\'re assessing AI security readiness for {{Company}}, I\'d be glad to share a brief overview or a 15-min call. No pressure.\n\nBest,\n{{YourName}}' },
          { type: 'Email', label: 'Follow-up', subject: 'Re: AI security readiness', text: 'Hi {{Name}},\n\nJust following up, if timing is better in a few weeks, happy to send over a one-pager on how we structure AI security training and certifications for enterprise teams. Reply with "send" and I\'ll get it across.\n\nBest,\n{{YourName}}' }
        ]
      },
      {
        persona: 'Enterprise — CTO / VP Engineering',
        templates: [
          { type: 'LinkedIn DM', label: 'Opener', text: 'Hi {{Name}}, quick check, do your teams already have AI-specific security training, or are you still relying on traditional AppSec/cloud guidance? We have the largest AI security training catalog, very practical and mapped to regulations, not checkbox.' },
          { type: 'LinkedIn DM', label: 'Follow-up', text: 'Hi {{Name}}, following up, we help teams build AI security capability without turning it into a massive program. Certifications are pooled and role-agnostic so you can use them where they matter. Happy to share a short example if useful.' },
          { type: 'Email', label: 'Intro', subject: 'AI security training, practical, not checkbox', text: 'Hi {{Name}},\n\nI know engineering leaders are under pressure to show AI risk is covered without slowing teams down.\n\nWe offer the largest AI security training catalog and certifications, practical, mapped to regulations, and designed so you can scale capability without a huge program. Many teams use us for audit readiness and to answer "how are we training on AI security?"\n\nIf you\'re evaluating options for {{Company}}, I\'d be glad to send a brief overview or jump on a short call.\n\nBest,\n{{YourName}}' }
        ]
      }
    ]
  }
}

const ICPHub = () => {
  const [searchParams] = useSearchParams()
  const productParam = searchParams.get('product')
  const [selectedProduct, setSelectedProduct] = useState(() =>
    productParam === 'aisec' || productParam === 'saas' ? productParam : 'aisec'
  )
  const [expandedSection, setExpandedSection] = useState('summary')
  const [copiedId, setCopiedId] = useState(null)

  useEffect(() => {
    if (productParam === 'aisec' || productParam === 'saas') {
      setSelectedProduct(productParam)
    }
  }, [productParam])

  const data = productData[selectedProduct] || productData.saas
  const visibleSections = data.sdrTemplates ? sections : sections.filter(s => s.id !== 'templates')

  useEffect(() => {
    if (expandedSection === 'templates' && !data.sdrTemplates) {
      setExpandedSection('summary')
    }
  }, [selectedProduct, data.sdrTemplates, expandedSection])

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="icp-hub-page">
      <div className="page-header">
        <div>
          <h2>🎯 Ideal Customer Profile</h2>
          <p className="subtitle">{data.subtitle}</p>
        </div>
      </div>

      {/* Product tabs */}
      <div className="icp-product-tabs">
        {PRODUCTS.map(product => (
          <button
            key={product.id}
            className={`product-tab ${selectedProduct === product.id ? 'active' : ''}`}
            onClick={() => setSelectedProduct(product.id)}
          >
            <span className="product-tab-icon">{product.icon}</span>
            <span className="product-tab-label">{product.name}</span>
          </button>
        ))}
      </div>

      <div className="icp-product-title-bar">
        <h3 className="icp-product-title">{data.title}</h3>
      </div>

      {/* Section tabs */}
      <div className="icp-nav-tabs">
        {visibleSections.map(section => (
          <button
            key={section.id}
            className={`icp-tab ${expandedSection === section.id ? 'active' : ''}`}
            onClick={() => setExpandedSection(section.id)}
          >
            <span className="tab-icon">{section.icon}</span>
            <span className="tab-label">{section.label}</span>
          </button>
        ))}
      </div>

      <div className="icp-content">
        {expandedSection === 'summary' && (
          <section className="icp-section">
            <h3>One-Paragraph ICP Summary</h3>
            <div className="summary-block">
              <p>{data.icpSummary}</p>
              <button className="copy-btn" onClick={() => copyToClipboard(data.icpSummary, 'summary')} title="Copy to clipboard">
                {copiedId === 'summary' ? '✓ Copied' : '📋 Copy'}
              </button>
            </div>
            {data.differentiator && (
              <div className="differentiator-callout">
                <strong>Key differentiator:</strong> {data.differentiator}
              </div>
            )}
            <div className="profile-grid">
              <div className="profile-card">
                <h4>Company / Customer Profile</h4>
                <ul>
                  {data.profile.company.map((item, i) => (
                    <li key={i}><strong>{item.strong}</strong> {item.text}</li>
                  ))}
                </ul>
              </div>
              <div className="profile-card">
                <h4>Buying Personas</h4>
                <ul>
                  {data.profile.personas.map((item, i) => (
                    <li key={i}><strong>{item.strong}</strong> {item.text}</li>
                  ))}
                </ul>
              </div>
              <div className="profile-card">
                <h4>Non-ICP (Disqualify)</h4>
                <ul>
                  {data.profile.nonIcp.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {expandedSection === 'messaging' && (
          <section className="icp-section">
            <h3>Messaging Map — Persona → Pains → Messages</h3>
            {data.messaging.map((block, i) => (
              <div key={i} className="persona-block">
                <h4>{block.persona}</h4>
                <div className="pains-list">
                  <strong>Top pains:</strong>
                  <ul>
                    {block.pains.map((p, j) => <li key={j}>{p}</li>)}
                  </ul>
                </div>
                <div className="messages-box">
                  <strong>Key messages:</strong>
                  <ul>
                    {block.messages.map((m, j) => <li key={j}>{m}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </section>
        )}

        {expandedSection === 'qualification' && (
          <section className="icp-section">
            <h3>Sales Qualification Checklist</h3>
            <p className="section-intro">Use in discovery calls or for lead scoring. Strong fit = most boxes checked.</p>
            <div className="qual-grid">
              <div className="qual-card">
                <h4>Firmographic / Fit</h4>
                {data.qualification.firmographic.map((item, i) => (
                  <label key={i} className="qual-item"><input type="checkbox" /><span>{item}</span></label>
                ))}
              </div>
              <div className="qual-card">
                <h4>Compliance / Regulatory</h4>
                {data.qualification.compliance.map((item, i) => (
                  <label key={i} className="qual-item"><input type="checkbox" /><span>{item}</span></label>
                ))}
              </div>
              <div className="qual-card">
                <h4>Pain Signals</h4>
                {data.qualification.pain.map((item, i) => (
                  <label key={i} className="qual-item"><input type="checkbox" /><span>{item}</span></label>
                ))}
              </div>
              <div className="qual-card">
                <h4>Buying Authority</h4>
                {data.qualification.authority.map((item, i) => (
                  <label key={i} className="qual-item"><input type="checkbox" /><span>{item}</span></label>
                ))}
              </div>
              <div className="qual-card disqualify">
                <h4>Disqualifiers</h4>
                {data.qualification.disqualify.map((item, i) => (
                  <label key={i} className="qual-item"><input type="checkbox" /><span>{item}</span></label>
                ))}
              </div>
            </div>
            <p className="qual-note">See also: <Link to="/bant-ref">BANT Reference</Link> for {data.bantLink} qualification.</p>
          </section>
        )}

        {expandedSection === 'pricing' && (
          <section className="icp-section">
            <h3>Pricing & Tier Alignment</h3>
            <div className="tier-cards">
              {data.tiers.map((tier, i) => (
                <div key={i} className="tier-card">
                  <h4>{tier.name}</h4>
                  <p className="tier-target"><strong>Target:</strong> {tier.target}</p>
                  <p className="tier-value"><strong>Value:</strong> {tier.value}{tier.valueNote ? ` — ${tier.valueNote}` : ''}</p>
                  <ul>
                    {tier.features.map((f, j) => <li key={j}>{f}</li>)}
                  </ul>
                  <p className="tier-positioning">{tier.positioning}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {expandedSection === 'channels' && (
          <section className="icp-section">
            <h3>Where to Find Your ICP</h3>
            {data.channels.map((ch, i) => (
              <div key={i} className="channel-block">
                <h4>{ch.title}</h4>
                {ch.content?.map((c, j) => (
                  <p key={j}>{c.strong && <strong>{c.strong} </strong>}{c.text}</p>
                ))}
                {ch.list && (
                  <ul>
                    {ch.list.map((item, j) => <li key={j}>{item}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </section>
        )}

        {expandedSection === 'website' && (
          <section className="icp-section">
            <h3>Website Structure (Quick Reference)</h3>
            <div className="website-structure">
              {data.website.map((ws, i) => (
                <div key={i} className="ws-block">
                  <span className="ws-num">{i + 1}</span>
                  <div>
                    <strong>{ws.title}</strong> — {ws.detail}
                    {ws.cta && <><br /><em>{ws.cta}</em></>}
                  </div>
                </div>
              ))}
            </div>
            {selectedProduct === 'aisec' && (
              <p className="website-links">
                <a href="https://www.aisectraining.com" target="_blank" rel="noopener noreferrer">aisectraining.com</a>
                {' · '}
                <a href="https://www.aisectraining.com/training" target="_blank" rel="noopener noreferrer">aisectraining.com/training</a>
              </p>
            )}
          </section>
        )}

        {expandedSection === 'triggers' && (
          <section className="icp-section">
            <h3>Core Buying Triggers</h3>
            <p className="section-intro">When these occur, your ICP is most likely to buy.</p>
            <div className="triggers-grid">
              {data.triggers.map((t, i) => (
                <div key={i} className="trigger-card">
                  <h4>{t.title}</h4>
                  <ul>
                    {t.items.map((item, j) => <li key={j}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {expandedSection === 'templates' && (
          <section className="icp-section">
            <h3>SDR Template Messages</h3>
            <p className="section-intro">Copy and personalise with {'{{Name}}'}, {'{{Company}}'}, {'{{YourName}}'} where needed.</p>
            {data.sdrTemplates ? (
              <div className="sdr-templates">
                {data.sdrTemplates.map((group, gIdx) => (
                  <div key={gIdx} className="sdr-persona-group">
                    <h4 className="sdr-persona-name">{group.persona}</h4>
                    <div className="sdr-template-list">
                      {group.templates.map((t, tIdx) => {
                        const copyId = `sdr-${selectedProduct}-${gIdx}-${tIdx}`
                        const fullText = t.subject ? `${t.subject}\n\n${t.text}` : t.text
                        return (
                          <div key={tIdx} className="sdr-template-card">
                            <div className="sdr-template-header">
                              <span className="sdr-template-type">{t.type}</span>
                              <span className="sdr-template-label">{t.label}</span>
                              <button
                                type="button"
                                className="sdr-copy-btn"
                                onClick={() => copyToClipboard(fullText, copyId)}
                                title="Copy to clipboard"
                              >
                                {copiedId === copyId ? '✓ Copied' : '📋 Copy'}
                              </button>
                            </div>
                            {t.subject && (
                              <div className="sdr-template-subject">
                                <strong>Subject:</strong> {t.subject}
                              </div>
                            )}
                            <div className="sdr-template-body">{t.text}</div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sdr-no-templates">No SDR templates for this product yet. Switch to AISec Training for template messages.</p>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

export default ICPHub
